import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { tables as mpTables } from '@sdk/multiplayer';
import type { OrientationVersion, AssignmentStatus } from '@/generated/minetwin/types';

/**
 * Story 7f — Live Sessions feed. Joins three tables into one row per
 * in-flight orientation run:
 *
 *   - `cohort_progress` (minetwin) filtered `status.tag === 'InProgress'`
 *   - `cohort_assignment_progress` (minetwin) for the current step + session_id
 *   - `discoverable_sessions` (mp, org-level) for the live mp session status
 *
 * The minetwin views are admin-scoped server-side via `admin_project_ids`,
 * so non-admin callers get an empty feed. The mp join is best-effort —
 * session_id == 0 (assigned but session not yet provisioned by the
 * scheduled procedure) or a session_id with no matching mp row both
 * surface as `mpSession: null` so the UI can degrade gracefully.
 */
export type LiveSession = {
  assignmentId: bigint;
  email: string;
  userId: string | null;
  orientationId: string;
  projectId: string;
  /** The cohort × orientation pairing this run belongs to, if any —
   *  feeds the monitoring pages' cohort filter (Phase 4, req 3). */
  cohortAssignmentId: bigint | null;
  orientationVersion: OrientationVersion;
  status: AssignmentStatus;
  assignedAtMs: number;
  startedAtMs: number | null;
  /** ms since `started_at` if started, else since `assigned_at`. */
  elapsedMs: number;
  currentStepId: string | null;
  currentPosition: number;
  enteredCurrentStepAtMs: number | null;
  sessionId: bigint;
  /** mp session row if one matches `sessionId`. Null when session_id == 0
   *  (not yet provisioned by `execute_assign_orientation`) or no mp row
   *  exists for the id. */
  mpSession: MpSessionRow | null;
};

type MpSessionRow = {
  sessionId: bigint;
  title: string | null;
  status: { tag: string };
  createdAtMs: number;
};

export function useLiveSessions(): {
  sessions: LiveSession[];
  isReady: boolean;
} {
  const [progress, progressReady] = useTable(
    'minetwin',
    minetwinTables.cohort_progress,
  );
  const [pointers, pointersReady] = useTable(
    'minetwin',
    minetwinTables.cohort_assignment_progress,
  );
  const [mpSessions, mpReady] = useTable(
    'mp',
    mpTables.discoverable_sessions,
  );

  const pointerByAssignment = useMemo(() => {
    const m = new Map<bigint, (typeof pointers)[number]>();
    for (const p of pointers) m.set(p.assignmentId, p);
    return m;
  }, [pointers]);

  const mpBySessionId = useMemo(() => {
    const m = new Map<string, MpSessionRow>();
    for (const s of mpSessions) {
      m.set(s.sessionId.toString(), {
        sessionId: s.sessionId,
        title: s.title ?? null,
        status: s.status,
        createdAtMs: toMillis(s.createdAt),
      });
    }
    return m;
  }, [mpSessions]);

  const sessions = useMemo<LiveSession[]>(() => {
    const now = Date.now();
    const out: LiveSession[] = [];
    for (const a of progress) {
      if (a.status.tag !== 'InProgress') continue;
      const p = pointerByAssignment.get(a.assignmentId);
      const assignedAtMs = toMillis(a.assignedAt);
      const startedAtMs = a.startedAt ? toMillis(a.startedAt) : null;
      const elapsedBase = startedAtMs ?? assignedAtMs;
      const sessionId = p?.sessionId ?? 0n;
      const mpSession =
        sessionId !== 0n ? mpBySessionId.get(sessionId.toString()) ?? null : null;
      out.push({
        assignmentId: a.assignmentId,
        email: a.email,
        userId: a.userId ?? null,
        orientationId: a.orientationId,
        projectId: a.projectId,
        cohortAssignmentId: a.cohortAssignmentId ?? null,
        orientationVersion: a.orientationVersion,
        status: a.status,
        assignedAtMs,
        startedAtMs,
        elapsedMs: Math.max(0, now - elapsedBase),
        currentStepId: p?.currentStepId ?? null,
        currentPosition: p?.currentPosition ?? 0,
        enteredCurrentStepAtMs: p?.enteredCurrentStepAt
          ? toMillis(p.enteredCurrentStepAt)
          : null,
        sessionId,
        mpSession,
      });
    }
    // Most recently started first; null startedAt sinks below started ones.
    out.sort((a, b) => (b.startedAtMs ?? 0) - (a.startedAtMs ?? 0));
    return out;
  }, [progress, pointerByAssignment, mpBySessionId]);

  return {
    sessions,
    isReady: progressReady && pointersReady && mpReady,
  };
}

/**
 * SpacetimeDB timestamps arrive as a `Timestamp` instance carrying
 * `__timestamp_micros_since_unix_epoch__` (micros since epoch). Older SDK
 * builds handed back a `Date`, a raw number, or a `{ micros }` object, so we
 * coerce every known shape to millis-since-epoch; unknown shapes fall back to 0.
 */
function toMillis(t: unknown): number {
  if (t == null) return 0;
  if (typeof t === 'number') return t;
  if (typeof t === 'bigint') return Number(t / 1000n);
  if (t instanceof Date) return t.getTime();
  if (typeof t === 'object') {
    const anyT = t as {
      micros?: bigint;
      microsSinceUnixEpoch?: bigint;
      __timestamp_micros_since_unix_epoch__?: bigint;
      getTime?: () => number;
      toDate?: () => Date;
    };
    if (typeof anyT.__timestamp_micros_since_unix_epoch__ === 'bigint')
      return Number(anyT.__timestamp_micros_since_unix_epoch__ / 1000n);
    if (typeof anyT.microsSinceUnixEpoch === 'bigint')
      return Number(anyT.microsSinceUnixEpoch / 1000n);
    if (typeof anyT.micros === 'bigint') return Number(anyT.micros / 1000n);
    if (typeof anyT.toDate === 'function') return anyT.toDate().getTime();
    if (typeof anyT.getTime === 'function') return anyT.getTime();
  }
  return 0;
}
